/**
 * A new event document — the ONE construction the admin panel
 * (`useEvents.createEvent`, and the editor's empty-form skeleton) and the MCP
 * server (`create_event_draft`) both run. Same reasoning as
 * `competitionDraft.js`: the MCP server writes with the Admin SDK, past the
 * security rules, so it must not hold a second idea of what a draft event is.
 *
 * Pure: the caller owns the document id (`"evt_" + Date.now()` on the
 * client), `createdBy` / `createdAt` / `updatedAt` (SDK sentinels) and the
 * actor stamp (`lastModifiedBy`, plus `lastModifiedVia` /
 * `lastModifiedByClient` on MCP writes).
 */

import { gymDefaultLang, localizedFromInput } from "./localizedText.js";
import { buildOrganizerContact } from "./organizerContact.js";

/** Default currency for new events. */
const DEFAULT_EVENT_CURRENCY = "HUF";

/** Default proxy limit per user per event. */
const DEFAULT_PROXY_LIMIT = 5;

/**
 * A fresh event doc skeleton — every field a new event starts with. The
 * editor also seeds its form from it (`{ ...makeEmptyEvent(), ...event }`), so
 * an older event missing a newer field still renders with the default.
 */
export function makeEmptyEvent() {
  return {
    name: "",
    description: "",
    descriptionI18n: {},
    longDescription: "",
    longDescriptionI18n: {},
    coverImageUrl: null,
    startDate: "",
    endDate: "",
    dailyStartTime: null,
    dailyEndTime: null,
    registrationStart: null,
    registrationDeadline: null,
    registrationClosed: false,
    cancellationDeadline: null,
    waitlistEnabled: true,
    autoPromoteFromWaitlist: true,
    allowGuests: true,
    allowProxyRegistration: true,
    proxyLimit: DEFAULT_PROXY_LIMIT,
    requireMinorData: false,
    entryFee: {
      enabled: false,
      amount: 0,
      currency: DEFAULT_EVENT_CURRENCY,
      note: "",
    },
    status: "draft",
    visibility: "public",
    // Who to call. OPTIONAL on an event, and with NO gym fallback (a
    // competition and a series have one) — see `resolveOrganizerContact`.
    organizerContact: null,
    groups: [],
    coaches: [],
    organizers: [],
    schedule: [],
    participantCounts: {},
  };
}

/**
 * Build a draft event: the skeleton with the input laid over it
 * (`{ ...makeEmptyEvent(), ...input }`, as the admin panel always did), and
 * `status` forced back to `"draft"` — the input cannot publish (the app only
 * ever creates with an empty input, then publishes from the editor).
 *
 * Returns the document without the caller-owned fields. The input is NOT
 * filtered: a caller taking fields from outside (the MCP tool) passes only the
 * fields it accepts.
 *
 * The two descriptions are the exception to "not filtered": they always go
 * through `localizedFromInput`, so the stored map and its mirror agree: a `…I18n`
 * map is the whole text, and without one a plain `description` means the gym's
 * default language — `gymCtx` is the gym the draft is for (`{ gymId, country }`),
 * and its `country` decides that language.
 */
export function buildEventDraft(input, gymCtx) {
  const data = input || {};
  const lang = gymDefaultLang(gymCtx);
  const text = (field) => localizedFromInput(data, field, lang);
  return {
    ...makeEmptyEvent(), ...data, ...text("description"), ...text("longDescription"),
    // Second exception to "not filtered": the contact always goes through the
    // normalizer, so a whitespace-only input is stored as `null`, not as a map
    // of blanks that every reader would have to unpack.
    organizerContact: buildOrganizerContact(data.organizerContact),
    status: "draft",
  };
}

/** A new event group's colour when the caller has none (the events module's green). */
export const DEFAULT_EVENT_GROUP_COLOR = "#059669";

/**
 * One entry of an event's inline `groups` array — the ONE shape every writer
 * of a new group uses: the editor's „+ Csoport", the template paste
 * (`templateToEventData`), the MCP draft tools' `addGroups` and the E2E seed.
 * There used to be three hand-written copies of these eight fields.
 *
 * Pure. The caller owns what differs between them: the `id` (`genId("grp")`
 * on the client, the MCP's `newId`), the `order` (the group's index), the
 * capacity default and the colour (the gym's accent, else
 * `DEFAULT_EVENT_GROUP_COLOR`). The age bounds are `null` when unset — never
 * `undefined`, which Firestore refuses — and the description an empty string.
 *
 * @param {{ id: string, name?: string, color?: string, maxParticipants: number,
 *   order?: number, ageMin?: number|null, ageMax?: number|null, description?: string }} g
 */
export function buildEventGroup(g) {
  return {
    id: g.id,
    name: g.name || "",
    color: g.color || DEFAULT_EVENT_GROUP_COLOR,
    maxParticipants: g.maxParticipants,
    order: g.order ?? 0,
    ageMin: g.ageMin ?? null,
    ageMax: g.ageMax ?? null,
    description: g.description || "",
  };
}
